/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loading } from '@app/components/common/Loading';
import { useSidebarNavigation } from '@app/hooks/useSidebarNavigation';

type ProtectedOutletProps = {
  requiredPath?: string;
};

export const ProtectedOutlet: React.FC<ProtectedOutletProps> = ({
  requiredPath,
}) => {
  const { pathname } = useLocation();
  const { navigation, loading, error } = useSidebarNavigation();

  const base = process.env.PUBLIC_URL || '';
  const currentPath =
    base && pathname.startsWith(base)
      ? pathname.slice(base.length) || '/'
      : pathname;

  const allowedPaths = useMemo(() => {
    const urls: string[] = [];

    const walk = (nodes?: { url?: string; children?: any[] }[]) => {
      if (!nodes) return;

      for (const n of nodes) {
        if (n.url) urls.push(n.url);
        if (n.children?.length) walk(n.children);
      }
    };

    walk(navigation);
    return urls;
  }, [navigation]);

  const targetPath = requiredPath || currentPath;

  const isAllowed = useMemo(() => {
    return allowedPaths.some((p) => {
      if (!p) return false;
      if (targetPath === p) return true;
      return targetPath.startsWith(p.endsWith('/') ? p : `${p}/`);
    });
  }, [allowedPaths, targetPath]);

  if (loading) return <Loading />;
  if (error) return <Navigate to="/server-error?c=acl" replace />;

  return isAllowed ? <Outlet /> : <Navigate to="/server-error?c=403" replace />;
};
